import { Injectable } from '@angular/core';
import { BackgroundGeolocationPlugin, Location } from '@capacitor-community/background-geolocation';
import { Observable, BehaviorSubject, from, EMPTY, of, interval, ReplaySubject, Subject } from 'rxjs';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { tap, filter, map, share, switchMap, take, catchError, auditTime, distinctUntilChanged, finalize, shareReplay, takeUntil } from 'rxjs/operators';
import { RIDE_POSITION_MINIMUM_ACCURACY_METERS$ } from 'libs/common/src/lib/constants/constants';
import { ToastrService } from 'ngx-toastr';
import { ModalService } from '@caballus/ui-common';

const BackgroundGeolocation = registerPlugin(
    'BackgroundGeolocation'
) as BackgroundGeolocationPlugin;

const SIMULATED_LOCATION_INTERVAL = 1000;
const LOCATION_UPDATE_INTERVAL = 1000; // 1 second interval

/**
 * A stateful filter for values of type T
 *
 * Takes an input value and returns an altered copy of that value based on the
 * behavior of the filter. May use internal mutability.
 *
 * @typeParam T type of the values being filtered
 */
interface Filter<T> {
    /**
     * Takes a value and returns an altered copy while altering any internal
     * state as necessary.
     */
    filter(value: T): T;
}

/**
 * Internal state for LocationFilter
 *
 * @internal
 */
interface LocationFilterState {
    timestamp: number;
    latitude: number;
    longitude: number;
    altitude: number;
    covariance: number;
}

class LocationFilter implements Filter<Location> {
    private state: LocationFilterState | null = null;

    /**
     * Pass in a location to process it and return the corrected location
     */
    public filter(location: Location): Location {
        // Minimum accuracy to prevent overvaluing more accurate coordinates
        //
        // I think? Can't tell if it actually makes a difference or not.
        //
        // example:
        // const MINIMUM_ACCURACY = 15;
        // const accuracy = Math.min(location.accuracy, MINIMUM_ACCURACY);
        const accuracy = location.accuracy;
        const measuredVariance = Math.pow(accuracy, 2);
        if (!this.state) {
            this.state = {
                timestamp: location.time,
                latitude: location.latitude,
                longitude: location.longitude,
                altitude: location.altitude,
                covariance: measuredVariance
            };

            // Can't do any filtering with just the first point
            return location;
        }

        // PREDICT PHASE

        const state = this.state;
        const deltaT = location.time - state.timestamp;
        if (deltaT < 0) {
            // Ignore out of order points
            return location;
        }

        // Uncertainty goes up with passed time
        //
        // TODO Maybe switch speed factors depending on gait... which is
        // dependent on speed. Will need to test and see if the cyclic nature
        // of that will cause issues.
        //
        // Integrating speed will likely involve adding another dimension to
        // the accuracy factor and change the math into linear algebra so that
        // will be a last resort.
        const speed = 3; // meters per second
        const covariance = state.covariance + (deltaT / 1000) * Math.pow(speed, 2);

        // UPDATE PHASE

        // TODO Use velocity (speed + bearing) information to better predict
        // location
        //
        // e.g. state = [x, y, vx, vy] or [lat, long, speed, bearing]
        // see note above about increased complexity

        // Kalman gain matrix
        // K = Covarariance * Inverse(Covariance + MeasurementVariance)
        //
        // NOTE: because K is dimensionless, it doesn't matter that
        // variance has different units to lat and lng
        const K = covariance / (covariance + measuredVariance);
        const correctedLatitude = state.latitude + K * (location.latitude - state.latitude);
        const correctedLongitude = state.longitude + K * (location.longitude - state.longitude);

        // Altitude has its own accuracy value that could likely be put into a
        // separate filter but I think the accuracy is congruent enough to the
        // location accuracy that we can lump it into the same correction
        // factor.
        const correctedAltitude = state.altitude + K * (location.altitude - state.altitude);

        this.state = {
            timestamp: location.time,
            latitude: correctedLatitude,
            longitude: correctedLongitude,
            altitude: correctedAltitude,
            // new Covarariance matrix is (IdentityMatrix - K) * Covariance
            covariance: (1 - K) * covariance
        };

        // END FILTER

        return {
            ...location,
            latitude: correctedLatitude,
            longitude: correctedLongitude,
            altitude: correctedAltitude
        };
    }
}

const WATCHER_OPTIONS = {
    backgroundTitle: 'Mapping Your Ride',
    backgroundMessage: 'Using your location',
    requestPermissions: true,
    stale: false,
    // The distance filtering is going to be done manually, and will happen
    // after accuracy filtering
    distanceFilter: 0
};

function simulatedLocation(): Location {
    function pad(value: number, scale: number) {
        const padding = Math.random() - 0.5;
        const paddedValue = value + padding * scale;
        
        return paddedValue;
    }
    return {
        latitude: pad(43.60744144799341, 0.0001),
        longitude: pad(-116.5052570155064, 0.0001),
        accuracy: 0,
        altitude: pad(2730, 1),
        altitudeAccuracy: 0,
        bearing: 0,
        speed: 0,
        time: new Date().getTime(),
        simulated: true
    };
}

@Injectable({ providedIn: 'root' })
export class LocationService {
    private readonly _locationSubject$ = new BehaviorSubject<Location>(null);
    private readonly _positionAccuracy$ = new BehaviorSubject<number>(null);
    private readonly _watcherId$ = new BehaviorSubject<string>(null);
    public endStream$ = new BehaviorSubject<boolean>(false);
    private readonly _simulateEndStream$ = new Subject<void>();
    private _subscriptionCount = 0;
    private readonly _locationFilter = new LocationFilter();

    public readonly position$ = this._locationSubject$.asObservable().pipe(
        filter(location => !!location),
        shareReplay({
            bufferSize: 1,
            refCount: true
        }),
        tap({
            subscribe: () => {
                if (this._subscriptionCount === 0) {
                    this.startLocationStream();
                }
                this._subscriptionCount++;
            },
            unsubscribe: () => {
                this._subscriptionCount--;
                if (this._subscriptionCount === 0) {
                    this.endLocationStream();
                }
            },
        })
    );

    public readonly accuracy$ = this._positionAccuracy$.asObservable();

    constructor(
        private readonly _toastService: ToastrService,
        private readonly _modalService: ModalService
    ) {}

    private createWatcher(): void {
        if (this._watcherId$.getValue()) {
            return;
        }

        BackgroundGeolocation.addWatcher(WATCHER_OPTIONS, (position, error) => {
            if (error) {
                if (error.code === 'NOT_AUTHORIZED') {
                    this.handleLocationPermissionError();
                }
                
                return
            }
            if (this.endStream$.getValue()) {
                this.endLocationStream();
                this.endStream$.next(false);
                return;
            }
            const filteredPosition = this._locationFilter.filter(position);
            this._locationSubject$.next(filteredPosition);
        }).then(watcherId => {
            if(this._watcherId$.getValue()) {
                //sometimes they race, not sure why
                BackgroundGeolocation.removeWatcher({ id: this._watcherId$.getValue() });
            } else {
                this._watcherId$.next(watcherId);
            }
        });
    }

    private handleLocationPermissionError(): void {
        this._modalService
            .confirm(
                'Unable to Locate',
                'This app needs your location, but does not have permission.',
                'Open settings now?'
            )
            .afterClosed()
            .pipe(
                switchMap(res => res ? BackgroundGeolocation.openSettings() : EMPTY)
            ).subscribe();
    }

    private createLocationStream(): void {
        if (Capacitor.isPluginAvailable('BackgroundGeolocation')) {
            this.createWatcher();
        } else {
            interval(SIMULATED_LOCATION_INTERVAL).pipe(
                map(() => simulatedLocation()),
                takeUntil(this._simulateEndStream$),
                tap(position => {
                    const filteredPosition = this._locationFilter.filter(position);
                    this._positionAccuracy$.next(filteredPosition.accuracy);
                    if (filteredPosition) {
                        this._locationSubject$.next(filteredPosition);
                    }
                }),
                map(() => void 0)
            ).subscribe();
        }
    }

    public startLocationStream(): void {
        this.createLocationStream();
    }

    public endLocationStream(): void {
        const watcherId = this._watcherId$.getValue();
        if (watcherId) {
            BackgroundGeolocation.removeWatcher({ id: watcherId })
                .then(() => {
                    this._watcherId$.next(null);
                })
            
        } 
        this._simulateEndStream$.next();
        
    }

}
